import { CalendarIcon } from '@icons'
import { Box, Text } from 'grommet'
import ReactDatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { Controller, useFormContext } from 'react-hook-form'
import './DateInputField.css'

interface DateInputProps {
  name: string
  label?: string
  errorMessage?: string
  placeholder?: string
  disabled?: boolean
  className?: string
  minDate?: Date
  maxDate?: Date
  showTimeSelect?: boolean
  showTimeSelectOnly?: boolean
  timeFormat?: string
  timeIntervals?: number
  dateFormat?: string
  showMonthDropdown?: boolean
  showYearDropdown?: boolean
  dropdownMode?: 'select' | 'scroll'
  yearDropdownItemNumber?: number
  scrollableYearDropdown?: boolean
  popperPlacement?: 'bottom-start' | 'bottom' | 'bottom-end' | 'top-start' | 'top' | 'top-end'
}

export const DateInputField = ({
  label,
  name,
  errorMessage,
  placeholder = 'dd/mm/yyyy',
  disabled = false,
  className = '',
  minDate,
  maxDate,
  showTimeSelect = false,
  timeFormat = 'HH:mm',
  timeIntervals = 15,
  dateFormat = 'dd/MM/yyyy',
  showMonthDropdown = true,
  showTimeSelectOnly,
  showYearDropdown = true,
  dropdownMode = 'select',
  yearDropdownItemNumber = 20,
  scrollableYearDropdown = true,
  popperPlacement,
  ...rest
}: DateInputProps) => {
  const {
    control,
    formState: { errors }
  } = useFormContext()

  const errorText = errors[name]?.message
  const displayError = errorText && typeof errorText === 'string' ? errorText : errorMessage

  const today = new Date()
  today.setFullYear(today.getFullYear() + 2)

  return (
    <div className={className}>
      {label && <p className="mb-[2px] text-sm font-medium">{label}</p>}

      <Controller
        control={control}
        name={name}
        render={({ field: { onChange, value } }) => (
          <div className="inline-block relative w-full">
            <ReactDatePicker
              className="!min-h-11"
              dateFormat={dateFormat}
              disabled={disabled}
              dropdownMode={dropdownMode}
              maxDate={maxDate || today}
              minDate={minDate || new Date(1920, 0, 1)}
              onChange={(date: Date | null) => onChange(date)}
              placeholderText={placeholder}
              popperPlacement={popperPlacement}
              scrollableYearDropdown={scrollableYearDropdown}
              selected={value ? new Date(value) : undefined}
              showMonthDropdown={showMonthDropdown}
              showTimeSelect={showTimeSelect}
              showTimeSelectOnly={showTimeSelectOnly}
              showYearDropdown={showYearDropdown}
              timeFormat={timeFormat}
              timeIntervals={timeIntervals}
              yearDropdownItemNumber={yearDropdownItemNumber}
              {...rest}
            />

            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#6B7280] pointer-events-none">
              <CalendarIcon />
            </div>
          </div>
        )}
      />

      {displayError && (
        <Box pad={{ left: 'small', top: 'xsmall' }}>
          <Text color="status-error" size="small">
            {displayError}
          </Text>
        </Box>
      )}
    </div>
  )
}
